import React, { useCallback, useEffect, useRef, useState } from 'react';
import type { Transition } from 'framer-motion';
import { motion, useReducedMotion } from 'framer-motion';
import { cn } from '@/lib/utils';
import { MagnifyingGlassAiIcon } from '@/utils/icons';
import { Badge } from '@/components/Badge';
import { KeyHint } from '@/components/KeyHint';
import { SearchPlaceholder } from './SearchPlaceholder';
import { useInlineCompletion } from './useInlineCompletion';

const LINE_HEIGHT_PX = 20;
const MAX_VISIBLE_LINES = 3.5;
const MAX_HEIGHT_PX = LINE_HEIGHT_PX * MAX_VISIBLE_LINES;
const COMPLETION_INLINE_MAX_CHARS = 80;

const TEXTAREA_RESIZE_TRANSITION: Transition = {
    type: 'tween',
    duration: 0.12,
    ease: [0.16, 1, 0.3, 1],
};

export type PromptTextFieldMode = 'single-line' | 'multiline';

export interface PromptTextFieldProps {
    value: string;
    onValueChange: (value: string) => void;
    onKeyDown: (
        e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>,
    ) => void;
    onPaste?: (
        e: React.ClipboardEvent<HTMLInputElement | HTMLTextAreaElement>,
    ) => void;
    inputRef: React.RefObject<HTMLInputElement | HTMLTextAreaElement | null>;
    prefix?: string;
    showPlaceholder?: boolean;
    placeholder?: string;
    completions?: string[];
    mode?: PromptTextFieldMode;
    searchIconLayoutId?: string;
    searchFieldLayoutId?: string;
    searchSharedLayoutTransition?: Transition;
    onSearchFocus?: () => void;
}

export function PromptTextField({
    value,
    onValueChange,
    onKeyDown,
    onPaste,
    inputRef,
    prefix,
    showPlaceholder = true,
    placeholder,
    completions = [],
    mode = 'multiline',
    searchIconLayoutId,
    searchFieldLayoutId,
    searchSharedLayoutTransition,
    onSearchFocus,
}: PromptTextFieldProps) {
    const [focused, setFocused] = useState(false);
    const [contentHeight, setContentHeight] = useState(LINE_HEIGHT_PX);
    const prefersReducedMotion = useReducedMotion();
    const multilineNodeRef = useRef<HTMLTextAreaElement | null>(null);
    const { completion, handleKeyDown } = useInlineCompletion(
        value,
        completions,
        onValueChange,
    );

    const assignParentRef = useCallback(
        (node: HTMLInputElement | HTMLTextAreaElement | null) => {
            const mutableRef = inputRef as React.MutableRefObject<
                HTMLInputElement | HTMLTextAreaElement | null
            >;
            mutableRef.current = node;
        },
        [inputRef],
    );

    const multilineCallbackRef = useCallback(
        (node: HTMLTextAreaElement | null) => {
            multilineNodeRef.current = node;
            assignParentRef(node);
        },
        [assignParentRef],
    );

    const singleLineCallbackRef = useCallback(
        (node: HTMLInputElement | null) => {
            assignParentRef(node);
        },
        [assignParentRef],
    );

    useEffect(() => {
        if (mode !== 'multiline') return;
        const textarea = multilineNodeRef.current;
        if (!textarea) return;

        textarea.style.height = `${LINE_HEIGHT_PX}px`;
        const targetHeight = Math.min(textarea.scrollHeight, MAX_HEIGHT_PX);
        textarea.style.height = `${targetHeight}px`;
        setContentHeight(targetHeight);
    }, [value, mode]);

    const isSingleLineCompletion =
        mode === 'multiline' &&
        !value.includes('\n') &&
        value.length < COMPLETION_INLINE_MAX_CHARS;

    const resizeTransition = prefersReducedMotion
        ? { duration: 0 }
        : TEXTAREA_RESIZE_TRANSITION;

    const handleFocus = () => {
        setFocused(true);
        onSearchFocus?.();
    };

    const handleBlur = () => {
        setFocused(false);
    };

    const iconNode = !prefix ? (
        <MagnifyingGlassAiIcon className="h-5 w-4 text-grey-300" />
    ) : null;

    const sharedLayoutTransition = searchSharedLayoutTransition
        ? { layout: searchSharedLayoutTransition }
        : undefined;

    const iconShell = iconNode ? (
        searchIconLayoutId ? (
            <motion.div
                layoutId={searchIconLayoutId}
                layout="position"
                className="shrink-0"
                transition={sharedLayoutTransition}
            >
                {iconNode}
            </motion.div>
        ) : (
            <div className="shrink-0">{iconNode}</div>
        )
    ) : null;

    const fieldInner = (
        <>
            {mode === 'multiline' ? (
                <motion.div
                    initial={false}
                    animate={{ height: contentHeight }}
                    transition={resizeTransition}
                >
                    <textarea
                        ref={multilineCallbackRef}
                        rows={1}
                        value={value}
                        onChange={(e) => onValueChange(e.target.value)}
                        className="m-0 block max-h-[70px] w-full resize-none overflow-y-auto bg-transparent p-0 font-geist text-sm font-normal leading-5 text-white-100 outline-none scrollbar-hide placeholder:text-grey-300 placeholder:leading-5"
                        onKeyDown={(e) => handleKeyDown(e, onKeyDown)}
                        placeholder={placeholder}
                        onPaste={onPaste}
                        onFocus={handleFocus}
                        onBlur={handleBlur}
                    />
                </motion.div>
            ) : (
                <input
                    ref={singleLineCallbackRef}
                    type="text"
                    size={1}
                    value={value}
                    onChange={(e) => onValueChange(e.target.value)}
                    className={cn(
                        'truncate bg-transparent w-full p-0 font-geist text-sm font-normal leading-5 text-white outline-none placeholder:leading-5',
                    )}
                    onKeyDown={(e) => handleKeyDown(e, onKeyDown)}
                    placeholder={placeholder}
                    onPaste={onPaste}
                    onFocus={handleFocus}
                    onBlur={handleBlur}
                />
            )}
            {completion &&
                (mode === 'single-line' || isSingleLineCompletion) && (
                    <div
                        className={
                            mode === 'single-line'
                                ? 'pointer-events-none absolute inset-0 flex items-center justify-start'
                                : 'pointer-events-none absolute left-0 top-0 flex h-5 w-full items-center'
                        }
                    >
                        <div className="flex h-5 w-full min-w-0 items-center whitespace-pre font-geist text-sm font-normal leading-5">
                            <span className="text-transparent">{value}</span>
                            <span className="text-grey-400">
                                {completion.slice(value.length)}
                            </span>
                            <KeyHint
                                theme="dark"
                                size="esc"
                                className={cn(
                                    'shrink-0',
                                    mode === 'multiline' ? 'ml-1.5' : 'ml-1',
                                )}
                            >
                                TAB
                            </KeyHint>
                        </div>
                    </div>
                )}
            {!value && showPlaceholder && !placeholder && (
                <SearchPlaceholder
                    focused={focused}
                    showShortcut={mode === 'multiline'}
                />
            )}
        </>
    );

    const fieldShell = searchFieldLayoutId ? (
        <motion.div
            layoutId={searchFieldLayoutId}
            layout="position"
            className={cn(
                'relative min-w-0 flex-1',
                mode === 'multiline' ? 'min-h-5' : 'flex h-full items-center',
            )}
            transition={sharedLayoutTransition}
        >
            {fieldInner}
        </motion.div>
    ) : (
        <div
            className={cn(
                'relative min-w-0 flex-1',
                mode === 'multiline' ? 'min-h-5' : 'flex h-full items-center',
            )}
        >
            {fieldInner}
        </div>
    );

    return (
        <div
            className={cn(
                'relative flex min-w-0 flex-1',
                mode === 'multiline'
                    ? 'flex items-center gap-4'
                    : 'flex h-full w-full min-w-0 items-center gap-1',
            )}
        >
            <div
                className={cn(
                    'relative flex min-w-0 flex-1',
                    mode === 'multiline'
                        ? 'items-start gap-1'
                        : 'items-center gap-1',
                )}
            >
                {iconShell}
                {prefix && (
                    <Badge variant="base" className="h-5 shrink-0 items-center">
                        {prefix}
                    </Badge>
                )}
                {fieldShell}
            </div>
        </div>
    );
}
